export type GemVariant = 'review' | 'complete';

export type GemIntensity = 'normal' | 'enhanced';

export type GemShape = 'brilliant' | 'emerald' | 'hex' | 'pear' | 'oval' | 'marquise' | 'princess';

export type GemFacet = {
  d: string;
  fill: string;
  opacity?: number;
};

export type GemSparkle = {
  cx: number;
  cy: number;
  size: number;
  opacity: number;
};

export type GemPalette = {
  base: string;
  mid: string;
  light: string;
  dark: string;
  glow: string;
};

export type GemSpec = {
  seed: string;
  variant: GemVariant;
  shape: GemShape;
  size: number;
  outlineD: string;
  facets: GemFacet[];
  sparkles: GemSparkle[];
  palette: GemPalette;
};

function hashSeed(input: string): number {
  let hash = 2166136261;

  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

function createRng(seed: string) {
  let state = hashSeed(seed) || 1;

  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value ^= value + Math.imul(value ^ (value >>> 7), 61 | value);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function pickOne<T>(items: T[], rng: () => number): T {
  return items[Math.floor(rng() * items.length)]!;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function hsl(h: number, s: number, l: number, alpha = 1) {
  if (alpha >= 1) {
    return `hsl(${Math.round(h)}, ${Math.round(s)}%, ${Math.round(l)}%)`;
  }

  return `hsla(${Math.round(h)}, ${Math.round(s)}%, ${Math.round(l)}%, ${alpha.toFixed(2)})`;
}

function scalePoints(points: Array<[number, number]>, size: number) {
  return points.map(([x, y]) => [x * size, y * size] as [number, number]);
}

function pointsToPath(points: Array<[number, number]>) {
  if (points.length === 0) {
    return '';
  }

  const [firstX, firstY] = points[0]!;
  const rest = points.slice(1).map(([x, y]) => `L ${x.toFixed(2)} ${y.toFixed(2)}`).join(' ');
  return `M ${firstX.toFixed(2)} ${firstY.toFixed(2)} ${rest} Z`;
}

function buildBrilliantOutline(size: number) {
  const points = scalePoints(
    [
      [0.5, 0.04],
      [0.78, 0.22],
      [0.92, 0.5],
      [0.72, 0.82],
      [0.5, 0.96],
      [0.28, 0.82],
      [0.08, 0.5],
      [0.22, 0.22],
    ],
    size,
  );

  return pointsToPath(points);
}

function buildEmeraldOutline(size: number) {
  const points = scalePoints(
    [
      [0.28, 0.08],
      [0.72, 0.08],
      [0.9, 0.34],
      [0.9, 0.66],
      [0.72, 0.92],
      [0.28, 0.92],
      [0.1, 0.66],
      [0.1, 0.34],
    ],
    size,
  );

  return pointsToPath(points);
}

function buildHexOutline(size: number) {
  const points = scalePoints(
    [
      [0.5, 0.06],
      [0.86, 0.28],
      [0.86, 0.72],
      [0.5, 0.94],
      [0.14, 0.72],
      [0.14, 0.28],
    ],
    size,
  );

  return pointsToPath(points);
}

function buildPearOutline(size: number) {
  const points = scalePoints(
    [
      [0.5, 0.04],
      [0.74, 0.18],
      [0.88, 0.42],
      [0.82, 0.68],
      [0.62, 0.88],
      [0.5, 0.96],
      [0.38, 0.88],
      [0.18, 0.68],
      [0.12, 0.42],
      [0.26, 0.18],
    ],
    size,
  );

  return pointsToPath(points);
}

function buildOvalOutline(size: number) {
  const cx = size * 0.5;
  const cy = size * 0.5;
  const rx = size * 0.34;
  const ry = size * 0.42;
  return `M ${cx - rx} ${cy} A ${rx} ${ry} 0 1 0 ${cx + rx} ${cy} A ${rx} ${ry} 0 1 0 ${cx - rx} ${cy} Z`;
}

function buildMarquiseOutline(size: number) {
  const points = scalePoints(
    [
      [0.5, 0.02],
      [0.68, 0.18],
      [0.92, 0.5],
      [0.68, 0.82],
      [0.5, 0.98],
      [0.32, 0.82],
      [0.08, 0.5],
      [0.32, 0.18],
    ],
    size,
  );

  return pointsToPath(points);
}

function buildPrincessOutline(size: number) {
  const points = scalePoints(
    [
      [0.5, 0.06],
      [0.78, 0.22],
      [0.94, 0.5],
      [0.78, 0.78],
      [0.5, 0.94],
      [0.22, 0.78],
      [0.06, 0.5],
      [0.22, 0.22],
    ],
    size,
  );

  return pointsToPath(points);
}

function buildOutline(shape: GemShape, size: number) {
  switch (shape) {
    case 'emerald':
      return buildEmeraldOutline(size);
    case 'hex':
      return buildHexOutline(size);
    case 'pear':
      return buildPearOutline(size);
    case 'oval':
      return buildOvalOutline(size);
    case 'marquise':
      return buildMarquiseOutline(size);
    case 'princess':
      return buildPrincessOutline(size);
    case 'brilliant':
    default:
      return buildBrilliantOutline(size);
  }
}

function buildPalette(variant: GemVariant, rng: () => number, intensity: GemIntensity = 'normal'): GemPalette {
  if (variant === 'review') {
    const hue = 205 + rng() * 18;
    const saturation = 12 + rng() * 10;
    const lightness = 58 + rng() * 8;

    return {
      base: hsl(hue, saturation, lightness),
      mid: hsl(hue, saturation - 2, lightness - 8),
      light: hsl(hue, saturation - 4, lightness + 10, 0.55),
      dark: hsl(hue, saturation + 2, lightness - 16),
      glow: hsl(hue, saturation, lightness, 0.12),
    };
  }

  const gemFamilies = [
    { hue: 350, name: 'ruby' },
    { hue: 225, name: 'sapphire' },
    { hue: 145, name: 'emerald' },
    { hue: 275, name: 'amethyst' },
    { hue: 32, name: 'topaz' },
    { hue: 190, name: 'aquamarine' },
  ];
  const family = pickOne(gemFamilies, rng);
  const hue = family.hue + (rng() - 0.5) * 16;
  const saturation = 58 + rng() * 24;
  const lightness = 44 + rng() * 12;

  return {
    base: hsl(hue, saturation, lightness),
    mid: hsl(hue + 8, saturation - 6, lightness + 6),
    light: hsl(hue - 12, saturation - 18, lightness + 28, intensity === 'enhanced' ? 0.92 : 0.82),
    dark: hsl(hue + 14, saturation + 8, lightness - 18),
    glow: hsl(hue, saturation, lightness + 18, intensity === 'enhanced' ? 0.42 : 0.28),
  };
}

function buildFacets(shape: GemShape, size: number, palette: GemPalette, rng: () => number): GemFacet[] {
  const center: [number, number] = [size * 0.5, size * 0.52];
  const facetCount = shape === 'hex' ? 6 : 5 + Math.floor(rng() * 3);
  const facets: GemFacet[] = [];

  facets.push({
    d: buildOutline(shape, size),
    fill: palette.base,
    opacity: 1,
  });

  for (let index = 0; index < facetCount; index += 1) {
    const angle = (Math.PI * 2 * index) / facetCount + rng() * 0.35;
    const reach = size * (0.22 + rng() * 0.16);
    const spread = size * (0.08 + rng() * 0.05);
    const tip: [number, number] = [
      center[0] + Math.cos(angle) * reach,
      center[1] + Math.sin(angle) * reach,
    ];
    const left: [number, number] = [
      center[0] + Math.cos(angle - 0.42) * spread,
      center[1] + Math.sin(angle - 0.42) * spread,
    ];
    const right: [number, number] = [
      center[0] + Math.cos(angle + 0.42) * spread,
      center[1] + Math.sin(angle + 0.42) * spread,
    ];

    facets.push({
      d: pointsToPath([tip, left, center, right]),
      fill: index % 2 === 0 ? palette.mid : palette.dark,
      opacity: 0.72 + rng() * 0.18,
    });
  }

  facets.push({
    d: pointsToPath([
      [size * 0.34, size * 0.18],
      [size * 0.58, size * 0.2],
      [size * 0.52, size * 0.38],
      [size * 0.3, size * 0.34],
    ]),
    fill: palette.light,
    opacity: 0.75,
  });

  if (shape === 'hex') {
    facets.push({
      d: pointsToPath([
        [size * 0.5, size * 0.12],
        [size * 0.66, size * 0.24],
        [size * 0.5, size * 0.36],
        [size * 0.34, size * 0.24],
      ]),
      fill: palette.light,
      opacity: 0.62,
    });
  }

  return facets;
}

function buildSparkles(size: number, variant: GemVariant, rng: () => number, intensity: GemIntensity = 'normal'): GemSparkle[] {
  if (variant !== 'complete') {
    return [];
  }

  const baseCount = intensity === 'enhanced' ? 4 : 2;
  const variance = intensity === 'enhanced' ? 3 : 2;
  const count = baseCount + Math.floor(rng() * variance);
  const opacityBoost = intensity === 'enhanced' ? 0.12 : 0;

  return Array.from({ length: count }, (_, index) => ({
    cx: size * (0.22 + rng() * 0.56),
    cy: size * (0.14 + rng() * 0.42),
    size: size * ((intensity === 'enhanced' ? 0.022 : 0.018) + rng() * 0.02),
    opacity: clamp(0.45 + rng() * 0.4 - index * 0.06 + opacityBoost, 0.2, 0.98),
  }));
}

export function generateGemSpec(
  seed: string,
  variant: GemVariant,
  size = 24,
  intensity: GemIntensity = 'normal',
): GemSpec {
  const rng = createRng(`${seed}:${variant}:${intensity}`);
  const shapes: GemShape[] = ['brilliant', 'emerald', 'hex', 'pear', 'oval', 'marquise', 'princess'];
  const shape = pickOne(shapes, rng);
  const palette = buildPalette(variant, rng, intensity);
  const outlineD = buildOutline(shape, size);

  return {
    seed,
    variant,
    shape,
    size,
    outlineD,
    facets: buildFacets(shape, size, palette, rng),
    sparkles: buildSparkles(size, variant, rng, intensity),
    palette,
  };
}
